import { useCallback, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { KanbanTask, KanbanTaskStatus } from '../../stores/KanbanTaskStores';
import type { TaskStore } from '../../stores/kanban/TaskStore';
import type { ColumnDropTarget, DropTarget } from './types';

export type BoardDnd = {
  draggedTask: KanbanTask | null;
  dropTarget: DropTarget;
  draggedColumn: string | null;
  columnDropTarget: ColumnDropTarget;
  registerColumn: (statusId: string, el: HTMLDivElement | null) => void;
  registerCard: (taskId: string, el: HTMLDivElement | null) => void;
  onCardDragStart: (e: DragEvent<HTMLDivElement>, task: KanbanTask) => void;
  onCardDragEnd: () => void;
  /** `tasks` is the column's rendered list — the insertion index is measured against those cards. */
  onColumnDragOver: (e: DragEvent<HTMLDivElement>, statusId: string, tasks: KanbanTask[]) => void;
  onColumnDragEnter: (e: DragEvent<HTMLDivElement>, statusId: string, tasks: KanbanTask[]) => void;
  onColumnDrop: (e: DragEvent<HTMLDivElement>, statusId: string) => Promise<void>;
  onHeaderDragStart: (e: DragEvent<HTMLDivElement>, statusId: string) => void;
  onHeaderDragEnd: () => void;
  onHeaderDragOver: (e: DragEvent<HTMLDivElement>, index: number) => void;
  onHeaderDrop: (e: DragEvent<HTMLDivElement>, index: number) => Promise<void>;
};

/** Native HTML5 drag and drop for cards (between and inside columns) and for whole columns. */
export function useBoardDnd(store: TaskStore, statuses: KanbanTaskStatus[], isReadOnly: boolean): BoardDnd {
  const { t } = useTranslation();
  const [draggedTask, setDraggedTask] = useState<KanbanTask | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [columnDropTarget, setColumnDropTarget] = useState<ColumnDropTarget>(null);
  const columnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const registerColumn = useCallback((statusId: string, el: HTMLDivElement | null) => {
    columnRefs.current[statusId] = el;
  }, []);

  const registerCard = useCallback((taskId: string, el: HTMLDivElement | null) => {
    cardRefs.current[taskId] = el;
  }, []);

  const onCardDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, task: KanbanTask) => {
      if (isReadOnly) return;
      setDraggedTask(task);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('taskId', task.id.toString());
    },
    [isReadOnly],
  );

  const onCardDragEnd = useCallback(() => {
    setDraggedTask(null);
    setDropTarget(null);
  }, []);

  const onColumnDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, statusId: string, tasks: KanbanTask[]) => {
      if (isReadOnly) return;
      e.preventDefault();
      const container = columnRefs.current[statusId];
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const offsetY = e.clientY - rect.top + container.scrollTop;
      // Insert before the first card whose middle the pointer has not passed; otherwise at the end.
      let newIndex = tasks.length;
      for (let i = 0; i < tasks.length; i++) {
        const cardEl = cardRefs.current[String(tasks[i].id)];
        if (!cardEl) continue;
        const cardRect = cardEl.getBoundingClientRect();
        const cardTop = cardRect.top - rect.top + container.scrollTop;
        if (offsetY < cardTop + cardRect.height / 2) {
          newIndex = i;
          break;
        }
      }
      setDropTarget({ statusId, index: newIndex });
    },
    [isReadOnly],
  );

  const onColumnDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>, statusId: string, tasks: KanbanTask[]) => {
      if (isReadOnly) return;
      e.preventDefault();
      if (tasks.length === 0) setDropTarget({ statusId, index: 0 });
    },
    [isReadOnly],
  );

  const onColumnDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>, statusId: string) => {
      if (isReadOnly) return;
      e.preventDefault();
      if (!draggedTask || dropTarget === null) return;
      try {
        await store.moveTask(draggedTask.id.toString(), statusId, dropTarget.index);
      } catch (error) {
        console.error('Error moving task:', error);
        message.error(t('FailedToReorder'));
      } finally {
        setDraggedTask(null);
        setDropTarget(null);
      }
    },
    [isReadOnly, draggedTask, dropTarget, store, t],
  );

  const onHeaderDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, statusId: string) => {
      // Columns used to be draggable for viewers too; the reorder then failed at the server.
      if (isReadOnly) return;
      setDraggedColumn(statusId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('statusId', statusId);
    },
    [isReadOnly],
  );

  const onHeaderDragEnd = useCallback(() => {
    setDraggedColumn(null);
    setColumnDropTarget(null);
  }, []);

  const onHeaderDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      if (!draggedColumn) return;
      e.preventDefault();
      setColumnDropTarget({ index });
    },
    [draggedColumn],
  );

  const onHeaderDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      if (!draggedColumn || columnDropTarget === null) return;
      const currentIndex = statuses.findIndex((status) => status.id === draggedColumn);
      if (currentIndex !== -1 && currentIndex !== index) {
        try {
          await store.updateStatusOrder(draggedColumn, index);
          message.success(t('ColumnReordered'));
        } catch (error) {
          console.error('Error reordering column:', error);
          message.error(t('FailedToReorderColumn'));
        }
      }
      setDraggedColumn(null);
      setColumnDropTarget(null);
    },
    [draggedColumn, columnDropTarget, statuses, store, t],
  );

  return {
    draggedTask,
    dropTarget,
    draggedColumn,
    columnDropTarget,
    registerColumn,
    registerCard,
    onCardDragStart,
    onCardDragEnd,
    onColumnDragOver,
    onColumnDragEnter,
    onColumnDrop,
    onHeaderDragStart,
    onHeaderDragEnd,
    onHeaderDragOver,
    onHeaderDrop,
  };
}
